/**
 * Resolves the Activity dates that should be displayed on timeline surfaces
 * (gantt charts, the activities table, PPT exports).
 *
 * Date precedence, resolved per field rather than per pair:
 * - Use startDate when available; otherwise use plannedStartDate.
 * - Use endDate when available; otherwise use plannedEndDate.
 *
 * Resolving per field is deliberate: a study that has started but not finished
 * has an actual startDate and a null endDate, and should render from its real
 * start to its planned end. Falling back as a pair would discard the known
 * actual start. Callers are expected to mark planned-derived values in the UI —
 * `startDateType`/`endDateType` say which side of the fallback each value came
 * from.
 *
 * Ported from the PPT export helper of the same name so both paths share one
 * definition of "the dates to show for this activity".
 */

export type ActivityDateType = 'actual' | 'planned' | 'missing'

export type ActivityDateValue = string | Date | null | undefined

export interface ActivityTimelineDateSource {
    startDate?: ActivityDateValue
    endDate?: ActivityDateValue
    plannedStartDate?: ActivityDateValue
    plannedEndDate?: ActivityDateValue
}

export interface ResolvedActivityTimelineDates {
    resolvedStartDate: ActivityDateValue
    resolvedEndDate: ActivityDateValue
    startDateType: ActivityDateType
    endDateType: ActivityDateType
}

/**
 * Resolves each end of an activity's timeline independently.
 *
 * Use this when a surface shows one side of the range on its own — the
 * activities table's completion date, for instance. Anything drawing a bar
 * wants {@link resolveActivityDateRange}, which also guarantees the values
 * are parseable.
 *
 * @param {ActivityTimelineDateSource} activity - Activity carrying any of the four date fields.
 * @returns {ResolvedActivityTimelineDates} The resolved value and origin for each end.
 */
export function resolveActivityTimelineDates(activity: ActivityTimelineDateSource = {}): ResolvedActivityTimelineDates {
    const actualStartDate = activity?.startDate || null
    const actualEndDate = activity?.endDate || null

    const plannedStartDate = activity?.plannedStartDate || null
    const plannedEndDate = activity?.plannedEndDate || null

    return {
        resolvedStartDate: actualStartDate || plannedStartDate,
        resolvedEndDate: actualEndDate || plannedEndDate,
        startDateType: actualStartDate ? 'actual' : plannedStartDate ? 'planned' : 'missing',
        endDateType: actualEndDate ? 'actual' : plannedEndDate ? 'planned' : 'missing',
    }
}

export interface ActivityDateRange {
    start: number
    end: number
    /** True when either end fell back to a planned date. */
    isPlanned: boolean
    startDateType: ActivityDateType
    endDateType: ActivityDateType
}

const toTimestamp = (value: ActivityDateValue): number | null => {
    if (!value) return null

    const time = new Date(value as string | Date).getTime()

    return Number.isNaN(time) ? null : time
}

/**
 * Resolves an activity to a drawable timestamp range, or null when it has no
 * usable one. Returning null in a single place is what lets callers skip the
 * activity without a separate "is it complete" check, and it rejects
 * unparseable dates that would otherwise become NaN bar coordinates.
 *
 * @param {ActivityTimelineDateSource} activity - Activity carrying any of the four date fields.
 * @returns {ActivityDateRange | null} Range with both ends parsed, or null if either end is missing or invalid.
 */
export function resolveActivityDateRange(activity: ActivityTimelineDateSource = {}): ActivityDateRange | null {
    const { resolvedStartDate, resolvedEndDate, startDateType, endDateType } = resolveActivityTimelineDates(activity)

    const start = toTimestamp(resolvedStartDate)
    const end = toTimestamp(resolvedEndDate)

    if (start === null || end === null) return null

    return {
        start,
        end,
        isPlanned: startDateType === 'planned' || endDateType === 'planned',
        startDateType,
        endDateType,
    }
}

/**
 * Formats a date as its calendar quarter, read in UTC.
 *
 * Activity dates are stored at UTC midnight, so reading the month and year with
 * local getters puts anywhere west of UTC a day behind — a January 1 date
 * renders as Q4 of the previous year. `quarterOfYear` in utilities/helpers uses
 * local getters and still has that problem for its other callers.
 *
 * @param {Date} date - The date to format.
 * @returns {string} The quarter and year, e.g. "Q1, 2026".
 */
export function formatQuarterUTC(date: Date): string {
    return `Q${Math.floor(date.getUTCMonth() / 3) + 1}, ${date.getUTCFullYear()}`
}

/**
 * Returns `color` at the given opacity, for rendering planned-derived gantt bars
 * as a translucent version of their series color. Only 6-digit hex is converted;
 * any other format (named colors, rgb(), gradients) is returned unchanged so an
 * unexpected value renders opaque rather than disappearing.
 *
 * @param {string} color - The series color to make translucent.
 * @param {number} alpha - Opacity between 0 and 1.
 * @returns {string} The color with an alpha channel applied, or the input unchanged.
 */
export function hexWithAlpha(color: string, alpha: number): string {
    if (!/^#[0-9a-f]{6}$/i.test(color)) return color

    const clamped = Math.min(Math.max(alpha, 0), 1)
    const alphaHex = Math.round(clamped * 255)
        .toString(16)
        .padStart(2, '0')

    return `${color}${alphaHex}`
}

/** Opacity applied to gantt bars whose start or end came from a planned date. */
export const PLANNED_BAR_ALPHA = 0.55
